<template>
  <div class="rcs-step rcs-step--carrier">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.subtitle') }}
    </p>

    <!-- Points of contact -->
    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.sections.pointsOfContact.title') }}
      </h3>
      <p class="rcs-carrier-section__lead">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.sections.pointsOfContact.lead') }}
      </p>

      <div class="rcs-carrier-repeater">
        <div
          v-for="(contact, contactIndex) in carrier.points_of_contact"
          :key="`contact-${contactIndex}`"
          class="rcs-carrier-repeater__card"
        >
          <div class="rcs-carrier-repeater__card-header">
            <p class="rcs-carrier-repeater__card-title">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.labels.contact', { n: contactIndex + 1 }) }}
            </p>
            <button
              v-if="carrier.points_of_contact.length > 1"
              type="button"
              class="rcs-carrier-repeater__remove"
              @click="removeContact(contactIndex)"
            >
              <i class="fa-solid fa-trash-can" aria-hidden="true" />
              {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.actions.remove') }}
            </button>
          </div>
          <div class="rcs-form-grid">
            <div class="rcs-form-grid__field">
              <label :for="`rcs-contact-first-${contactIndex}`">
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.firstName') }}
              </label>
              <InputText
                :id="`rcs-contact-first-${contactIndex}`"
                v-model="contact.first_name"
                maxlength="200"
              />
            </div>
            <div class="rcs-form-grid__field">
              <label :for="`rcs-contact-last-${contactIndex}`">
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.lastName') }}
              </label>
              <InputText
                :id="`rcs-contact-last-${contactIndex}`"
                v-model="contact.last_name"
                maxlength="200"
              />
            </div>
            <div class="rcs-form-grid__field rcs-form-grid__field--full">
              <label :for="`rcs-contact-job-${contactIndex}`">
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.jobTitle') }}
              </label>
              <InputText
                :id="`rcs-contact-job-${contactIndex}`"
                v-model="contact.job_title"
                maxlength="200"
              />
            </div>
            <div class="rcs-form-grid__field">
              <label :for="`rcs-contact-email-${contactIndex}`">
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.email') }}
              </label>
              <InputText
                :id="`rcs-contact-email-${contactIndex}`"
                v-model="contact.email"
                type="email"
                maxlength="200"
              />
            </div>
            <div class="rcs-form-grid__field">
              <label :for="`rcs-contact-website-${contactIndex}`">
                {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.brandWebsite') }}
              </label>
              <InputText
                :id="`rcs-contact-website-${contactIndex}`"
                v-model="contact.brand_website"
                type="url"
                maxlength="500"
                :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrier.placeholders.brandWebsite')"
              />
            </div>
          </div>
        </div>
        <div class="rcs-carrier-repeater__actions">
          <button
            type="button"
            class="rcs-carrier-repeater__add"
            @click="addContact"
          >
            <i class="fa-solid fa-plus" aria-hidden="true" />
            {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.actions.addContact') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Traffic estimates -->
    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.sections.trafficEstimates.title') }}
      </h3>
      <p class="rcs-carrier-section__lead">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.sections.trafficEstimates.lead') }}
      </p>
      <div class="rcs-form-grid">
        <div class="rcs-form-grid__field">
          <label for="rcs-average-global-traffic">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.averageGlobalTraffic') }}
          </label>
          <Dropdown
            id="rcs-average-global-traffic"
            v-model="carrier.traffic_estimates.average_global_traffic"
            :options="averageGlobalTrafficOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrier.placeholders.selectOption')"
          />
        </div>
        <div class="rcs-form-grid__field">
          <label for="rcs-message-rate">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.averageMessageRate') }}
          </label>
          <Dropdown
            id="rcs-message-rate"
            v-model="carrier.traffic_estimates.average_message_rate_per_user"
            :options="messageRateOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrier.placeholders.selectOption')"
          />
        </div>
        <div class="rcs-form-grid__field">
          <label for="rcs-users-targeted">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.fields.usersTargeted') }}
          </label>
          <Dropdown
            id="rcs-users-targeted"
            v-model="carrier.traffic_estimates.users_targeted"
            :options="usersTargetedOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.carrier.placeholders.selectOption')"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import {
  RCS_AVERAGE_GLOBAL_TRAFFIC,
  RCS_AVERAGE_MESSAGE_RATE_PER_USER,
  RCS_USERS_TARGETED,
  applyCarrierHiddenDefaults,
  createEmptyPointOfContact,
} from '@/types/rcs-onboarding';
import type { RcsDedicatedFormState } from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsCarrierRequirementsStep',

  components: {
    InputText,
    Dropdown,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const carrier = computed(() => props.form.carrier_requirements);

    const buildEnumOptions = (prefix: string, values: readonly string[]) => values.map(
      (value) => ({
        label: t(`storeParameters.services.rcsServiceParameter.wizard.carrier.enums.${prefix}.${value}`),
        value,
      }),
    );

    const averageGlobalTrafficOptions = computed(() => buildEnumOptions(
      'averageGlobalTraffic',
      RCS_AVERAGE_GLOBAL_TRAFFIC,
    ));

    const messageRateOptions = computed(() => buildEnumOptions(
      'averageMessageRatePerUser',
      RCS_AVERAGE_MESSAGE_RATE_PER_USER,
    ));

    const usersTargetedOptions = computed(() => buildEnumOptions(
      'usersTargeted',
      RCS_USERS_TARGETED,
    ));

    const addContact = () => {
      carrier.value.points_of_contact.push(createEmptyPointOfContact());
    };

    const removeContact = (index: number) => {
      carrier.value.points_of_contact.splice(index, 1);
    };

    onMounted(() => {
      applyCarrierHiddenDefaults(props.form.carrier_requirements);
    });

    return {
      t,
      carrier,
      averageGlobalTrafficOptions,
      messageRateOptions,
      usersTargetedOptions,
      addContact,
      removeContact,
    };
  },
});
</script>
